/**
 * HOA Discount Arbiter — site-wide discount code (LABOR15)
 * ================================================================================
 * Goes in the THEME (runs for every visitor, layout/theme.liquid).
 *
 * BEHAVIOUR: LABOR15 is applied to the cart immediately on every page load —
 * no Intelligems dependency, no wait. LABOR15 is the site-wide offer and all
 * storefront copy advertises "code LABOR15, auto-applied at checkout".
 *
 * The retired AYURVEDA10 code is listed as LEGACY_CODES: it is never applied,
 * only STRIPPED from the cart. Returning shoppers whose persistent cart still
 * carries AYURVEDA10 get it swapped for LABOR15 on their next page view.
 * (The old 15% vs 10% Intelligems A/B plumbing has been removed — there is now
 * a single site-wide code, so window.tenPercentActive is ignored entirely.)
 *
 * Codes are applied to the CART itself (cart-level discount codes), so the
 * discount is visible store-wide: cart drawer, /cart page, and checkout.
 * Any other codes already on the cart (e.g. popup codes) are preserved; only
 * the legacy arbiter codes are swapped out.
 * If the cart had items and the code was newly applied, the cart drawer is
 * re-rendered via the Section Rendering API; on /cart the page reloads once.
 *
 * Debug: set window.__hoaArbiterDebug = true before this script runs.
 */
(function () {
  'use strict';

  if (window.__hoaDiscountArbiter) return; // idempotent per page load
  window.__hoaDiscountArbiter = true;

  var CODE = 'LABOR15'; // site-wide code (must exist as a discount CODE in admin)
  var LEGACY_CODES = ['AYURVEDA10']; // retired codes — removed from the cart, never applied

  var MAX_ATTEMPTS = 3;

  function log() {
    if (window.__hoaArbiterDebug && window.console) {
      console.log.apply(console, ['[hoa-arbiter]'].concat([].slice.call(arguments)));
    }
  }

  // Never run inside the theme editor.
  if (window.Shopify && window.Shopify.designMode) return;

  /* ------------------------------------------------------------- cart layer */

  function getCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error('cart.js ' + r.status);
      return r.json();
    });
  }

  function hasCode(cart, code) {
    return (cart.discount_codes || []).some(function (c) {
      return String(c.code).toUpperCase() === code.toUpperCase();
    });
  }

  function isLegacy(code) {
    return LEGACY_CODES.some(function (legacy) {
      return String(code).toUpperCase() === legacy.toUpperCase();
    });
  }

  function applyCode(target, attempt) {
    attempt = attempt || 1;
    return getCart().then(function (cart) {
      var carriesLegacy = (cart.discount_codes || []).some(function (c) { return isLegacy(c.code); });
      if (hasCode(cart, target) && !carriesLegacy) {
        log('code already on cart:', target);
        return { cart: cart, changed: false };
      }
      // Keep every foreign code (popup/email codes), drop the retired arbiter
      // codes, then add ours.
      var keep = (cart.discount_codes || [])
        .map(function (c) { return c.code; })
        .filter(function (c) { return !isLegacy(c) && String(c).toUpperCase() !== target.toUpperCase(); });
      keep.push(target);
      log('applying discount list:', keep.join(','), '(attempt ' + attempt + ')');
      return fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: keep.join(',') })
      }).then(function () {
        return getCart();
      }).then(function (cart2) {
        if (!hasCode(cart2, target)) throw new Error('code not on cart after apply');
        log('applied OK:', target);
        return { cart: cart2, changed: true };
      });
    }).catch(function (err) {
      log('apply failed:', err && err.message);
      if (attempt < MAX_ATTEMPTS) {
        return new Promise(function (res) { setTimeout(res, 600 * attempt); })
          .then(function () { return applyCode(target, attempt + 1); });
      }
      return { cart: null, changed: false };
    });
  }

  /* ------------------------------------------------- storefront UI refresh */

  function refreshUi(result) {
    if (!result || !result.changed || !result.cart || !result.cart.item_count) return;

    // On the cart page, a one-shot reload is the safest full refresh.
    if (/^\/(cart)\/?$/.test(location.pathname)) {
      log('on /cart with newly applied code → reload once');
      location.reload();
      return;
    }

    // Elsewhere: re-render the cart drawer section so an already-rendered
    // drawer shows the discounted totals without needing a navigation.
    try {
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      fetch(root + '?sections=cart-drawer', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (sections) {
          if (!sections || !sections['cart-drawer']) return;
          var current = document.getElementById('CartDrawer');
          var host = document.querySelector('cart-drawer');
          if (!current || !host) return;
          if (host.classList.contains('active')) return; // never yank an open drawer
          var doc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
          var fresh = doc.getElementById('CartDrawer');
          if (fresh) {
            current.innerHTML = fresh.innerHTML;
            var freshHost = doc.querySelector('cart-drawer');
            if (freshHost) host.className = freshHost.className;
            log('cart drawer re-rendered with discount');
          }
        })
        .catch(function () { /* non-fatal — next cart mutation re-renders anyway */ });
    } catch (e) { /* non-fatal */ }
  }

  /* ---------------------------------------------------------------- decide */

  function decide() {
    log('site-wide code →', CODE);
    applyCode(CODE).then(refreshUi);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', decide);
  } else {
    decide();
  }
})();
